import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Heart, ChevronRight, BookOpen, Sparkles, Shield, Sun, Flower2 } from 'lucide-react';

const SELFLOVE_CONTENT = [
  {
    id: 'basics',
    title: 'Vad är självkärlek?',
    icon: Heart,
    description: 'Grunderna i att ha en god relation till dig själv.',
    content: [
      '**Självkärlek är inte narcissism:** Det handlar om att behandla dig själv med samma vänlighet du skulle visa en god vän.',
      '**Det är en färdighet:** Självkärlek är något du lär dig och övar på – det kommer inte automatiskt.',
      '**Det påverkar allt:** Hur du ser på dig själv färgar alla dina relationer och livsval.',
      '**Du förtjänar det:** Oavsett vad du gjort eller inte gjort, förtjänar du kärlek – även från dig själv.',
      '**Det är inte egoistiskt:** Att ta hand om dig själv gör dig bättre på att ta hand om andra.',
      '**Det handlar om respekt:** Att sätta gränser, säga nej och prioritera ditt välmående.'
    ],
    questions: [
      'Hur pratar du med dig själv i ditt inre?',
      'Skulle du prata så med en vän?',
      'Vad behöver du förlåta dig själv för?',
      'Vilka behov ignorerar du ofta?'
    ]
  },
  {
    id: 'inner-critic',
    title: 'Tystna inre kritikern',
    icon: Shield,
    description: 'Hantera den röst som säger att du inte är bra nog.',
    content: [
      '**Alla har en inre kritiker:** Den rösten som dömer, jämför och aldrig är nöjd.',
      '**Den försöker skydda dig:** Kritikern utvecklades för att hålla dig säker från avvisning.',
      '**Ge den ett namn:** Kalla den något – det gör den mindre skrämmande. "Där är Negativa Nisse igen."',
      '**Ifrågasätt tankarna:** "Är det sant? Skulle jag säga så till en vän?"',
      '**Svara med medkänsla:** "Jag gör mitt bästa. Det är okej att inte vara perfekt."',
      '**Omformulera:** Istället för "jag är värdelös" – "jag hade en dålig dag, men det definierar inte mig."'
    ],
    questions: [
      'Vad säger din inre kritiker mest?',
      'Varifrån kommer den rösten?',
      'Hur kan du bemöta kritikern med vänlighet?',
      'Vad skulle du säga till ett barn som tänkte så om sig själv?'
    ]
  },
  {
    id: 'selfcare',
    title: 'Egenvård i praktiken',
    icon: Flower2,
    description: 'Konkreta sätt att ta hand om dig själv.',
    content: [
      '**Fysisk:** Sömn, mat, rörelse, vila. Grunderna för att må bra.',
      '**Emotionell:** Tillåt alla känslor. Skriv dagbok. Gråt när du behöver.',
      '**Social:** Umgås med de som får dig att må bra. Säg nej till energitjuvar.',
      '**Mental:** Begränsa stress. Ta pauser. Gör något kreativt.',
      '**Andlig:** Vad ger dig mening? Natur, meditation, musik, konst?',
      '**Gränser:** Säg nej utan skuld. Ditt välmående är inte förhandlingsbart.',
      '**Det behöver inte vara stort:** En kopp te, fem minuters stillhet, en promenad.'
    ],
    questions: [
      'Vilken sorts egenvård prioriterar du?',
      'Vilken försummar du?',
      'Vad kan du göra för dig själv idag?',
      'Vad hindrar dig från att ta hand om dig själv?'
    ]
  },
  {
    id: 'worthiness',
    title: 'Värdighet & tillräcklighet',
    icon: Sun,
    description: 'Att känna sig värdig kärlek och goda saker.',
    content: [
      '**Du är värdig nu:** Inte när du går ner i vikt, får jobbet eller hittar kärleken. Nu.',
      '**Värdighet är medfödd:** Du behöver inte bevisa den eller förtjäna den.',
      '**Skam ljuger:** Den rösten som säger att du är fel – den har fel.',
      '**Jämförelse stjäl glädje:** Din resa är din. Andras framgång minskar inte ditt värde.',
      '**Du räcker:** Exakt som du är, med dina fel och brister.',
      '**Självacceptans är frigörande:** Du behöver inte vara perfekt för att vara älskad.'
    ],
    questions: [
      'När känner du dig mest värdig?',
      'Vad triggar känslan av att inte räcka till?',
      'Vems röst hör du när du känner dig värdelös?',
      'Vad skulle förändras om du verkligen trodde att du räcker?'
    ]
  },
  {
    id: 'affirmations',
    title: 'Bekräftelser & mantran',
    icon: Sparkles,
    description: 'Ord att påminna dig själv om din värde.',
    content: [
      '**"Jag är tillräcklig precis som jag är."**',
      '**"Mina känslor är giltiga."**',
      '**"Jag förtjänar kärlek och respekt."**',
      '**"Det är okej att vila."**',
      '**"Jag gör mitt bästa, och det räcker."**',
      '**"Jag väljer att behandla mig själv med vänlighet."**',
      '**"Mina misstag definierar mig inte."**',
      '**"Jag är värd att ta plats."**',
      '**"Jag släpper det som inte tjänar mig."**',
      '**"Jag är på min egen sida."**',
      'Säg dem högt. Skriv dem. Upprepa tills de börjar kännas sanna.'
    ],
    questions: [
      'Vilken bekräftelse behöver du höra just nu?',
      'Finns det något mantran du redan använder?',
      'Hur känns det att säga något vänligt till dig själv?'
    ]
  }
];

export default function SelfLovePage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [selectedTopic, setSelectedTopic] = useState(null);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="selflove-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Självkärlek</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till självkärleksverktyg.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (selectedTopic) {
    const topic = SELFLOVE_CONTENT.find(t => t.id === selectedTopic);
    return (
      <div className="min-h-screen bg-background" data-testid="selflove-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedTopic(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Självkärlek</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-pink-50 rounded-2xl p-8">
            <topic.icon className="w-10 h-10 text-pink-600 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{topic.title}</h2>
            <p className="text-muted-foreground">{topic.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            {topic.content.map((text, i) => (
              <p key={i} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </div>

          <div className="bg-primary/5 rounded-2xl p-6 space-y-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Reflektionsfrågor
            </h3>
            {topic.questions.map((q, i) => (
              <div key={i} className="flex gap-3 items-start p-4 rounded-xl bg-white">
                <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">{i + 1}</span>
                <span>{q}</span>
              </div>
            ))}
          </div>

          {/* Contact */}
          
          <p className="text-xs text-muted-foreground text-center px-4">
            ⚕️ Om du kämpar med djup självkritik eller depression, sök professionell hjälp.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="selflove-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Självkärlek</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-3">
          <Heart className="w-12 h-12 mx-auto text-pink-500" />
          <h2 className="font-heading text-2xl font-semibold">Självkärlek</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Den viktigaste relationen du har är den med dig själv.
          </p>
        </div>

        <div className="space-y-3">
          {SELFLOVE_CONTENT.map((topic) => (
            <button
              key={topic.id}
              onClick={() => setSelectedTopic(topic.id)}
              className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center">
                  <topic.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{topic.title}</h3>
                  <p className="text-sm text-muted-foreground">{topic.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
              </div>
            </button>
          ))}
        </div>

        {/* Daily reminder */}
        <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-6 border border-pink-100 text-center">
          <Sparkles className="w-8 h-8 mx-auto text-pink-500 mb-3" />
          <h3 className="font-semibold text-lg mb-2">Dagens påminnelse</h3>
          <p className="text-pink-800 italic">
            "Du är värd all kärlek du så frikostigt ger till andra."
          </p>
        </div>

        {/* Contact */}
      </main>
    </div>
  );
}
